/**
 * Impostazioni, sezione «Profilo e accesso»: chi sei, l'abbonamento, la
 * password e dove sei entrato.
 */

import { useEffect, useState } from "react";
import { Check, CreditCard, KeyRound, LogOut, Monitor } from "lucide-react";
import { api, setToken } from "../../lib/api.ts";
import { messaggioDi } from "../../lib/errori.ts";
import { useStore } from "../../lib/store.tsx";
import { Badge, Card, Pill, cx } from "../../ui/primitives.tsx";
import { CampoPassword, Robustezza } from "../Accesso.tsx";
import { Scheda } from "./comuni.tsx";

export function SezioneProfilo() {
  const store = useStore();
  const utente = store.utente;
  const iniziale = (utente?.nome ?? utente?.email ?? "?")
    .slice(0, 1)
    .toUpperCase();

  return (
    <>
      <Card className="p-5 xl:col-span-2">
        <div className="flex items-center gap-4">
          <span className="grid size-14 shrink-0 place-items-center rounded-full bg-ink text-[22px] font-extrabold text-ink-inverse">
            {iniziale}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[17px] font-extrabold text-ink">
              {utente?.nome ?? "—"}
            </p>
            <p className="truncate text-[13.5px] text-ink-soft">
              {utente?.email}
            </p>
          </div>
          <div className="hidden text-right sm:block">
            <Badge tone={utente?.ruolo === "proprietario" ? "ok" : "plain"}>
              {utente?.ruolo === "collaboratore"
                ? "collaboratore"
                : "proprietario"}
            </Badge>
            <p className="mt-1 text-[12.5px] text-ink-faint">
              {store.account?.nome}
            </p>
          </div>
        </div>
      </Card>

      <CambiaPassword />
      <Accessi />
      {store.account?.abbonamento && utente?.ruolo !== "collaboratore" ? (
        <Abbonamento stato={store.account.abbonamento} />
      ) : null}
    </>
  );
}

/** Come lo dice Stripe, detto come lo direbbe una persona. */
const STATI_ABBONAMENTO: Record<
  string,
  { testo: string; tono: "ok" | "warn" }
> = {
  active: { testo: "attivo", tono: "ok" },
  trialing: { testo: "in prova", tono: "ok" },
  past_due: { testo: "pagamento in ritardo", tono: "warn" },
  unpaid: { testo: "non pagato", tono: "warn" },
  canceled: { testo: "disdetto", tono: "warn" },
  incomplete: { testo: "pagamento da completare", tono: "warn" },
  paused: { testo: "in pausa", tono: "warn" },
};

/**
 * Carta, fatture, disdetta: tutto sul portale di Stripe. Otto non tiene carte
 * e non rifà le sue pagine — ne rifarebbe una peggiore, e con i soldi dentro.
 */
function Abbonamento({ stato }: { stato: string }) {
  const store = useStore();
  const [inCorso, setInCorso] = useState(false);
  const detto = STATI_ABBONAMENTO[stato] ?? {
    testo: stato,
    tono: "warn" as const,
  };

  const apri = async () => {
    setInCorso(true);
    try {
      const { url } = await api.portaleAbbonamento();
      window.location.assign(url);
    } catch (errore) {
      store.notify(messaggioDi(errore));
      setInCorso(false);
    }
  };

  return (
    <Scheda
      Icona={CreditCard}
      titolo="Abbonamento"
      testo={
        <>
          <Badge tone={detto.tono}>{detto.testo}</Badge>{" "}
          {stato === "past_due"
            ? "L'ultimo pagamento non è passato: aggiorna la carta per non restare fermo."
            : "Cambia la carta, scarica le fatture o disdici. Si apre la pagina sicura di Stripe."}
        </>
      }
      azione={
        <Pill
          variant="secondary"
          size="sm"
          icon={<CreditCard size={14} />}
          onClick={apri}
          loading={inCorso}
        >
          Gestisci abbonamento
        </Pill>
      }
    />
  );
}

function CambiaPassword() {
  const store = useStore();
  const [aperto, setAperto] = useState(false);
  const [attuale, setAttuale] = useState("");
  const [nuova, setNuova] = useState("");
  const [conferma, setConferma] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const chiudi = () => {
    setAperto(false);
    setAttuale("");
    setNuova("");
    setConferma("");
    setErrore(null);
  };

  const salva = async (event: React.FormEvent) => {
    event.preventDefault();
    if (nuova !== conferma) {
      setErrore("Le due password nuove non coincidono.");
      return;
    }
    setErrore(null);
    setSalvando(true);
    try {
      await api.cambiaPassword(attuale, nuova);
      store.notify(
        "Password cambiata. Gli altri dispositivi dovranno rientrare.",
      );
      chiudi();
    } catch (e) {
      setErrore(messaggioDi(e));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Scheda
      Icona={KeyRound}
      titolo="Password"
      testo="Cambiandola, esci da tutti gli altri dispositivi. Resti dentro solo qui."
      azione={
        aperto ? null : (
          <Pill size="sm" variant="secondary" onClick={() => setAperto(true)}>
            Cambia password
          </Pill>
        )
      }
    >
      {aperto ? (
        <form
          onSubmit={salva}
          className="animate-rise flex max-w-[420px] flex-col gap-3.5"
        >
          <CampoPassword
            id="password-attuale"
            label="Password attuale"
            autoComplete="current-password"
            value={attuale}
            onChange={setAttuale}
            autoFocus
          />
          <CampoPassword
            id="password-nuova"
            label="Nuova password"
            autoComplete="new-password"
            value={nuova}
            onChange={setNuova}
          />
          <Robustezza password={nuova} />
          <CampoPassword
            id="password-conferma"
            label="Ripeti la nuova"
            autoComplete="new-password"
            value={conferma}
            onChange={setConferma}
          />
          {errore ? (
            <p
              role="alert"
              className="rounded-card bg-danger-soft px-3.5 py-2.5 text-[13px] font-semibold text-danger"
            >
              {errore}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Pill
              variant="primary"
              size="md"
              type="submit"
              loading={salvando}
              icon={<Check size={15} />}
            >
              Salva
            </Pill>
            <Pill variant="ghost" size="md" onClick={chiudi}>
              Annulla
            </Pill>
          </div>
        </form>
      ) : null}
    </Scheda>
  );
}

function Accessi() {
  const store = useStore();
  const [accessi, setAccessi] = useState<Array<{
    ip: string;
    riuscito: boolean;
    at: string;
  }> | null>(null);
  const [confermaUscita, setConfermaUscita] = useState(false);

  useEffect(() => {
    void api
      .accessi()
      .then((r) => setAccessi(r.accessi))
      .catch(() => setAccessi([]));
  }, []);

  const esciDaTutti = async () => {
    try {
      await api.logoutTutti();
      setToken(null);
      window.location.reload();
    } catch (e) {
      store.notify(messaggioDi(e));
    }
  };

  const quando = (iso: string) =>
    new Intl.DateTimeFormat("it-IT", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Rome",
    }).format(new Date(iso));

  return (
    <Scheda
      Icona={Monitor}
      titolo="Accessi recenti"
      testo="Gli ultimi tentativi di entrare con la tua email. Se ne vedi uno che non è tuo, cambia la password."
    >
      {accessi == null ? (
        <p className="text-[13px] text-ink-faint">Carico…</p>
      ) : accessi.length === 0 ? (
        <p className="text-[13px] text-ink-faint">Nessun accesso registrato.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line rounded-card border border-line">
          {accessi.map((a) => (
            <li
              key={a.at}
              className="flex items-center gap-3 px-3.5 py-2.5 text-[13px]"
            >
              <span
                className={cx(
                  "size-2 shrink-0 rounded-full",
                  a.riuscito ? "bg-ok" : "bg-danger",
                )}
              />
              <span className="flex-1 font-semibold text-ink">
                {a.riuscito ? "Entrato" : "Password sbagliata"}
              </span>
              <span className="tabular-nums text-ink-faint">{a.ip}</span>
              <span className="w-28 text-right tabular-nums text-ink-soft">
                {quando(a.at)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {confermaUscita ? (
          <>
            <span className="text-[13px] font-semibold text-ink">
              Esci da tutti i dispositivi, compreso questo?
            </span>
            <Pill
              size="sm"
              variant="danger"
              onClick={esciDaTutti}
              icon={<LogOut size={14} />}
            >
              Sì, esci ovunque
            </Pill>
            <Pill
              size="sm"
              variant="ghost"
              onClick={() => setConfermaUscita(false)}
            >
              No
            </Pill>
          </>
        ) : (
          <Pill
            size="sm"
            variant="secondary"
            onClick={() => setConfermaUscita(true)}
            icon={<LogOut size={14} />}
          >
            Esci da tutti i dispositivi
          </Pill>
        )}
      </div>
    </Scheda>
  );
}
